import { ReactNode } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Skeleton,
  Stack,
  SxProps,
  TableCell,
  TableRow,
  Theme,
  Typography,
} from "@mui/material";

// Shared building blocks for the dashboard tabs (Overview / Orders / Returns).

export const SectionCard = ({
  title,
  subtitle,
  action,
  children,
  noPadding,
}: {
  title?: string;
  subtitle?: string;
  action?: ReactNode;
  children: ReactNode;
  noPadding?: boolean;
}) => (
  <Paper
    variant="outlined"
    sx={{ borderRadius: 3, height: "100%", overflow: "hidden" }}
  >
    {title && (
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ px: 2.5, pt: 2, pb: noPadding ? 1.5 : 0 }}
      >
        <Box>
          <Typography fontWeight={600} fontSize={16}>
            {title}
          </Typography>
          {subtitle && (
            <Typography fontSize={12} color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        {action}
      </Stack>
    )}
    <Box sx={noPadding ? undefined : { p: 2.5 }}>{children}</Box>
  </Paper>
);

export const StatTile = ({
  label,
  value,
  caption,
  color = "#27374d",
  icon,
  loading,
  compact,
}: {
  label: string;
  value: ReactNode;
  caption?: ReactNode;
  color?: string;
  icon?: ReactNode;
  loading?: boolean;
  /** Lighter, smaller variant for dense pages. */
  compact?: boolean;
}) => (
  <Paper
    variant="outlined"
    sx={{
      borderRadius: 3,
      p: compact ? 2 : 2.5,
      height: "100%",
      transition: "box-shadow .2s, transform .2s",
      "&:hover": { boxShadow: "0 6px 20px rgba(39,55,77,.08)", transform: "translateY(-2px)" },
    }}
  >
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1.5}>
      <Box minWidth={0}>
        <Typography fontSize={13} color="text.secondary" fontWeight={500}>
          {label}
        </Typography>
        <Typography
          fontSize={compact ? 19 : 24}
          fontWeight={compact ? 600 : 700}
          lineHeight={1.25}
          mt={0.5}
          sx={{ fontVariantNumeric: "tabular-nums" }}
          noWrap={compact}
        >
          {loading ? <Skeleton width={60} /> : value}
        </Typography>
        {caption && (
          <Typography fontSize={12} color="text.secondary" mt={0.5}>
            {caption}
          </Typography>
        )}
      </Box>
      {icon && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: compact ? 36 : 44,
            height: compact ? 36 : 44,
            "& svg": compact ? { fontSize: 20 } : undefined,
            flexShrink: 0,
            borderRadius: "50%",
            color,
            bgcolor: `${color}1a`,
          }}
        >
          {icon}
        </Box>
      )}
    </Stack>
  </Paper>
);

// Stacked bar + legend showing how a total splits across statuses.
export const StatusBreakdown = ({
  items,
  loading,
}: {
  items: { label: string; value: number | undefined; color: string }[];
  loading?: boolean;
}) => {
  const sum = items.reduce((s, i) => s + (i.value ?? 0), 0);
  const pct = (v: number | undefined) => (sum ? Math.round(((v ?? 0) / sum) * 100) : 0);

  if (loading) return <Skeleton variant="rounded" height={110} />;

  return (
    <Stack gap={2.5}>
      <Box
        sx={{
          display: "flex",
          gap: "3px",
          height: 12,
          borderRadius: 6,
          overflow: "hidden",
          bgcolor: "#eceff3",
        }}
      >
        {items.map((i) =>
          i.value ? (
            <Box
              key={i.label}
              title={`${i.label}: ${formatNumber(i.value)}`}
              sx={{ flex: i.value, bgcolor: i.color, minWidth: 6 }}
            />
          ) : null
        )}
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, 1fr)",
            sm: `repeat(${Math.min(items.length, 3)}, 1fr)`,
            md: `repeat(${items.length}, 1fr)`,
          },
          gap: 2,
        }}
      >
        {items.map((i) => (
          <Box key={i.label}>
            <Stack direction="row" alignItems="center" gap={0.75}>
              <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: i.color }} />
              <Typography fontSize={12} color="text.secondary">
                {i.label}
              </Typography>
            </Stack>
            <Typography fontSize={20} fontWeight={700} mt={0.25}>
              {formatNumber(i.value)}
              <Typography component="span" fontSize={12} color="text.secondary" fontWeight={500} ml={0.75}>
                {pct(i.value)}%
              </Typography>
            </Typography>
          </Box>
        ))}
      </Box>
    </Stack>
  );
};

export const FilterSelect = ({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string | undefined;
  onChange: (event: SelectChangeEvent<string>) => void;
  options: { value: string; label: string }[];
}) => (
  <FormControl size="small" sx={{ minWidth: 150 }}>
    <InputLabel>{label}</InputLabel>
    <Select label={label} value={value ?? ""} onChange={onChange}>
      {options.map((o) => (
        <MenuItem key={o.value} value={o.value}>
          {o.label}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

export const FilterBar = ({ children }: { children: ReactNode }) => (
  <Stack
    direction="row"
    flexWrap="wrap"
    alignItems="center"
    gap={2}
    sx={{ mb: 3 }}
  >
    {children}
  </Stack>
);

// Compact, readable table styling shared by all dashboard tables.
export const tableSx: SxProps<Theme> = {
  "& th": {
    bgcolor: "#f6f8fa",
    fontWeight: 600,
    fontSize: 12,
    color: "text.secondary",
    whiteSpace: "nowrap",
  },
  "& td, & th": { py: 1.2 },
  "& tbody tr:hover": { bgcolor: "#fafbfc" },
  "& tbody tr:last-of-type td": { borderBottom: 0 },
};

// Placeholder rows shown while a table's data is loading.
export const TableLoading = ({ cols, rows = 3 }: { cols: number; rows?: number }) => (
  <>
    {Array.from({ length: rows }).map((_, r) => (
      <TableRow key={r}>
        {Array.from({ length: cols }).map((_, c) => (
          <TableCell key={c}>
            <Skeleton />
          </TableCell>
        ))}
      </TableRow>
    ))}
  </>
);

export const STATUS_COLORS = {
  total: "#27374d",
  pending: "#ed6c02",
  transit: "#0288d1",
  exception: "#d32f2f",
  delivered: "#2e7d32",
  rto: "#7b1fa2",
  cancelled: "#757575",
};

export const formatNumber = (n: number | undefined | null) =>
  (n ?? 0).toLocaleString("en-IN");

// DM Sans (the app font) has no proper ₹ glyph, so render the symbol in a system font.
export const formatCurrency = (n: number | undefined | null) => (
  <>
    <span style={{ fontFamily: "Roboto, Arial, sans-serif" }}>₹</span>
    {(n ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}
  </>
);

export const formatPercent = (part: number | undefined, whole: number | undefined) =>
  whole ? `${(((part ?? 0) / whole) * 100).toFixed(1)}%` : "0%";
